import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import authService from '../services/authService';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const loginMutation = useMutation({
    mutationFn: (credentials) => authService.login(credentials.email, credentials.password),
    onSuccess: () => {
      navigate('/');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }

    if (!email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }

    toast.promise(
      loginMutation.mutateAsync({ email, password }),
      {
        pending: {
          render: 'Logging in...',
        },
        success: {
          render: 'Login successful!',
        },
        error: {
          render({ data }) {
            if (Array.isArray(data?.message)) {
              return data.message[0] || 'Login failed';
            }
            return data?.message || 'Login failed. Please try again.';
          },
        },
      }
    );
  };


  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-6 sm:mb-8">
          <img 
            src="/images/Fade no bg.png" 
            alt="Project Prose Logo" 
            className="h-16 sm:h-20 w-auto mx-auto mb-2 object-contain"
          />
          <h1 className="text-3xl sm:text-4xl font-display font-bold text-prose-brand mb-1 sm:mb-2">
            Project Prose
          </h1>
          <p className="text-xs sm:text-sm text-prose-brand/60">Admin Dashboard</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-lg border border-gray-300 p-4 sm:p-6 md:p-8">
          <h2 className="text-xl sm:text-2xl font-bold text-prose-brand mb-4 sm:mb-6">Admin Login</h2>

          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
            {/* Email Field */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-prose-black mb-1 sm:mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-2.5 sm:top-3 text-prose-brand/60 flex-shrink-0" size={18} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@example.com"
                  className="w-full pl-9 sm:pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand focus:border-transparent text-sm"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-prose-black mb-1 sm:mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-2.5 sm:top-3 text-prose-brand/60 flex-shrink-0" size={18} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-9 sm:pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand focus:border-transparent text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-2.5 sm:top-3 text-prose-brand/60 hover:text-prose-brand flex-shrink-0"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>


            {/* Login Button */}
            <button
              type="submit"
              disabled={loginMutation.isPending}
              className="w-full bg-prose-brand hover:bg-prose-brand/80 text-white font-semibold py-2 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand focus:ring-offset-2 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
            >
              {loginMutation.isPending ? 'Logging in...' : 'Login'}
            </button>
          </form>

        </div>

        {/* Footer */}
        <div className="text-center mt-4 sm:mt-6">
          <p className="text-prose-brand/60 text-xs sm:text-sm">
            © 2026 Project Prose. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
